import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  KeyRound,
  MapPinned,
  Search,
  ShieldCheck,
  UserPlus,
  UserRoundCog,
  X,
} from "lucide-react";
import { useRef, useState, type FormEvent } from "react";
import { PageHeader } from "../components/PageHeader";
import { RetryButton, StatePanel } from "../components/StatePanel";
import { api, downloadFile, formatDate, mutationKey } from "../lib/api";

interface ReferenceOption {
  readonly id: string;
  readonly name: string;
}

interface PlantingHistory {
  readonly planting: Readonly<Record<string, unknown>>;
  readonly request: Readonly<Record<string, unknown>>;
  readonly assignment: Readonly<Record<string, unknown>>;
  readonly assignment_versions: readonly Readonly<Record<string, unknown>>[];
  readonly allowance_ledger: readonly Readonly<Record<string, unknown>>[];
  readonly posts: readonly Readonly<Record<string, unknown>>[];
  readonly decisions: readonly Readonly<Record<string, unknown>>[];
  readonly point_ledger: readonly Readonly<Record<string, unknown>>[];
  readonly badge_awards: readonly Readonly<Record<string, unknown>>[];
  readonly leaderboard_snapshots: readonly Readonly<Record<string, unknown>>[];
}

function formString(data: FormData, name: string): string {
  const value = data.get(name);
  return typeof value === "string" ? value : "";
}

const historySections = [
  ["assignment_versions", "Assignment versions"],
  ["allowance_ledger", "Allowance ledger"],
  ["posts", "Planting posts"],
  ["decisions", "Verification decisions"],
  ["point_ledger", "Point ledger"],
  ["badge_awards", "Badge awards"],
  ["leaderboard_snapshots", "Leaderboard snapshots"],
] as const satisfies ReadonlyArray<
  readonly [
    keyof Omit<PlantingHistory, "planting" | "request" | "assignment">,
    string,
  ]
>;

function recordText(
  record: Readonly<Record<string, unknown>>,
  key: string,
): string | null {
  const value = record[key];
  return typeof value === "string" || typeof value === "number"
    ? String(value)
    : null;
}

function historyRecordLabel(
  section: (typeof historySections)[number][0],
  record: Readonly<Record<string, unknown>>,
): string {
  const labels: Readonly<Record<typeof section, readonly string[]>> = {
    assignment_versions: ["version", "state"],
    allowance_ledger: ["entry_kind", "quantity"],
    posts: ["post_type", "state"],
    decisions: ["decision", "reason_code"],
    point_ledger: ["rule_code", "amount"],
    badge_awards: ["badge_definition_id", "badge_version"],
    leaderboard_snapshots: ["entity_type", "period_key", "rank"],
  };
  const summary = labels[section]
    .map((key) => recordText(record, key))
    .filter((value): value is string => value !== null)
    .map(readableValue)
    .join(" · ");
  return summary === ""
    ? (recordText(record, "id") ?? "Connected record")
    : summary;
}

function historyRecordDate(
  record: Readonly<Record<string, unknown>>,
): string | null {
  for (const key of [
    "created_at",
    "changed_at",
    "captured_at",
    "posted_at",
    "earned_at",
    "calculated_at",
  ]) {
    const value = recordText(record, key);
    if (value !== null) return formatDate(value);
  }
  return null;
}

function ReferenceSelect({
  name,
  label,
  options,
  defaultValue,
  emptyLabel = "All",
}: {
  readonly name: string;
  readonly label: string;
  readonly options: readonly ReferenceOption[] | undefined;
  readonly defaultValue?: string;
  readonly emptyLabel?: string;
}) {
  return (
    <label>
      {label}
      <select name={name} defaultValue={defaultValue}>
        <option value="">{emptyLabel}</option>
        {options?.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))}
      </select>
    </label>
  );
}

const accessRoles = [
  "planter",
  "manager",
  "verifier",
  "administrator",
  "auditor",
] as const;

interface AdminPerson {
  readonly id: string;
  readonly display_name: string;
  readonly account_kind: string;
  readonly status: string;
  readonly roles: readonly string[];
  readonly scopes: readonly {
    readonly id: string;
    readonly role: string;
    readonly organization_id?: string | null;
    readonly organization_name: string | null;
    readonly municipality_id?: string | null;
    readonly municipality_name: string | null;
    readonly site_id?: string | null;
    readonly site_name: string | null;
  }[];
}

function readableValue(value: string): string {
  return value.replaceAll("_", " ");
}

export function ReportsPage() {
  const [exportId, setExportId] = useState<string | null>(null);
  const [appliedFilters, setAppliedFilters] = useState<
    Readonly<Record<string, string>>
  >({});
  const reportForm = useRef<HTMLFormElement>(null);
  const filterQuery = new URLSearchParams(appliedFilters).toString();
  const dashboard = useQuery({
    queryKey: ["dashboard", appliedFilters],
    queryFn: () =>
      api<{
        readonly metrics: Readonly<Record<string, number>>;
        readonly condition_distribution: readonly {
          readonly condition: string;
          readonly count: number;
        }[];
        readonly return_reasons: readonly {
          readonly reason_code: string;
          readonly count: number;
        }[];
        readonly verifier_throughput: readonly {
          readonly actor_user_id: string;
          readonly verifier_name: string;
          readonly decisions: number;
          readonly average_review_hours: number | null;
        }[];
        readonly calculated_at: string;
      }>(`/admin/dashboard${filterQuery === "" ? "" : `?${filterQuery}`}`),
  });
  const reference = useQuery({
    queryKey: ["reference"],
    queryFn: () =>
      api<{
        readonly organizations: readonly ReferenceOption[];
        readonly municipalities: readonly ReferenceOption[];
        readonly barangays: readonly ReferenceOption[];
        readonly sites: readonly ReferenceOption[];
        readonly plant_types: readonly (ReferenceOption & {
          readonly common_name: string;
        })[];
        readonly planting_cohorts: readonly ReferenceOption[];
      }>("/reference-data"),
  });
  const createExport = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<{ readonly id: string; readonly state: string }>("/admin/exports", {
        method: "POST",
        idempotencyKey: mutationKey("export"),
        body: JSON.stringify(body),
      }),
    onSuccess: (job) => setExportId(job.id),
  });
  const exportJob = useQuery({
    queryKey: ["export", exportId],
    queryFn: () =>
      api<{
        readonly id: string;
        readonly state: string;
        readonly download_path: string | null;
        readonly expires_at: string | null;
      }>(`/admin/exports/${exportId ?? ""}`),
    enabled: exportId !== null,
    refetchInterval: (query) =>
      query.state.data?.state === "ready" ? false : 2_000,
  });
  const downloadExport = useMutation({
    mutationFn: (path: string) => downloadFile(path),
    onSuccess: ({ blob, filename }) => {
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = filename;
      anchor.hidden = true;
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
    },
  });
  const filtersFrom = (form: HTMLFormElement) => {
    const data = new FormData(form);
    return Object.fromEntries(
      [
        "municipality_id",
        "barangay_id",
        "site_id",
        "plant_type_id",
        "planting_cohort_id",
        "organization_id",
        "manager_id",
        "planter_id",
        "starts_at",
        "ends_at",
      ]
        .map((key) => [key, formString(data, key)] as const)
        .filter(([, value]) => value !== "")
        .map(([key, value]) => [
          key,
          key === "starts_at" || key === "ends_at"
            ? new Date(value).toISOString()
            : value,
        ]),
    );
  };
  const applyFilters = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setAppliedFilters(filtersFrom(event.currentTarget));
  };
  const queueExport = () => {
    if (reportForm.current === null) return;
    const data = new FormData(reportForm.current);
    createExport.mutate({
      report_type: data.get("report_type"),
      filters: filtersFrom(reportForm.current),
    });
  };
  return (
    <>
      <PageHeader
        eyebrow="Reporting"
        title="Program reports"
        description="Filter environmental outcomes by municipality, barangay, site, plant type, cohort, organization, manager, planter, and date."
      />
      {dashboard.isLoading ? (
        <StatePanel
          kind="loading"
          title="Loading program measures"
          detail="Calculating the current scoped operational view."
        />
      ) : dashboard.isError || dashboard.data === undefined ? (
        <StatePanel
          kind="error"
          title="Dashboard unavailable"
          detail="No report was changed."
          action={<RetryButton onClick={() => void dashboard.refetch()} />}
        />
      ) : (
        <div className="metric-grid" aria-label="Filtered program measures">
          {Object.entries(dashboard.data.metrics).map(([key, value]) => (
            <article className="metric-card" key={key}>
              <span>{key.replaceAll("_", " ")}</span>
              <strong>{value.toLocaleString()}</strong>
            </article>
          ))}
        </div>
      )}
      {dashboard.data === undefined ? null : (
        <section className="panel report-breakdowns">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Operational detail</p>
              <h2>Outcomes and review capacity</h2>
            </div>
            <span className="quiet">
              {Object.keys(appliedFilters).length === 0
                ? "Current authorized scope"
                : `${Object.keys(appliedFilters).length} filters applied`}
            </span>
          </div>
          <div className="report-insight-grid">
            <article>
              <h3>Verified condition</h3>
              {dashboard.data.condition_distribution.length === 0 ? (
                <p className="quiet">No verified condition evidence.</p>
              ) : (
                <div className="condition-list">
                  {dashboard.data.condition_distribution.map((item) => (
                    <div key={item.condition}>
                      <span>{readableValue(item.condition)}</span>
                      <strong>{item.count.toLocaleString()}</strong>
                    </div>
                  ))}
                </div>
              )}
            </article>
            <article>
              <h3>Return reasons</h3>
              {dashboard.data.return_reasons.length === 0 ? (
                <p className="quiet">No returned evidence in this view.</p>
              ) : (
                <div className="condition-list">
                  {dashboard.data.return_reasons.map((item) => (
                    <div key={item.reason_code}>
                      <span>{readableValue(item.reason_code)}</span>
                      <strong>{item.count.toLocaleString()}</strong>
                    </div>
                  ))}
                </div>
              )}
            </article>
            <article>
              <h3>Verifier throughput</h3>
              {dashboard.data.verifier_throughput.length === 0 ? (
                <p className="quiet">No review decisions in this view.</p>
              ) : (
                <div className="throughput-list">
                  {dashboard.data.verifier_throughput.map((item) => (
                    <div key={item.actor_user_id}>
                      <span>{item.verifier_name}</span>
                      <strong>
                        {item.decisions.toLocaleString()} decisions
                      </strong>
                      <small>
                        {item.average_review_hours === null
                          ? "Turnaround unavailable"
                          : `${item.average_review_hours.toFixed(1)} average hours`}
                      </small>
                    </div>
                  ))}
                </div>
              )}
            </article>
          </div>
        </section>
      )}
      <section className="panel">
        <h2>Scoped CSV export</h2>
        <p>
          Generation runs in the background. Files expire and creation and
          download are recorded in the audit log.
        </p>
        <form className="report-form" ref={reportForm} onSubmit={applyFilters}>
          <label>
            Report
            <select name="report_type">
              <option value="plantings">Planting outcomes</option>
            </select>
          </label>
          <ReferenceSelect
            name="organization_id"
            label="Organization"
            options={reference.data?.organizations}
          />
          <ReferenceSelect
            name="municipality_id"
            label="Municipality"
            options={reference.data?.municipalities}
          />
          <ReferenceSelect
            name="barangay_id"
            label="Barangay"
            options={reference.data?.barangays}
          />
          <ReferenceSelect
            name="site_id"
            label="Site"
            options={reference.data?.sites}
          />
          <ReferenceSelect
            name="plant_type_id"
            label="Plant type"
            options={reference.data?.plant_types.map((item) => ({
              id: item.id,
              name: item.common_name,
            }))}
          />
          <ReferenceSelect
            name="planting_cohort_id"
            label="Cohort"
            options={reference.data?.planting_cohorts}
          />
          <label>
            Manager ID
            <input name="manager_id" />
          </label>
          <label>
            Planter ID
            <input name="planter_id" />
          </label>
          <label>
            From
            <input name="starts_at" type="datetime-local" />
          </label>
          <label>
            To
            <input name="ends_at" type="datetime-local" />
          </label>
          <button className="button primary" type="submit">
            Apply filters
          </button>
          <button
            className="button secondary"
            type="button"
            disabled={createExport.isPending}
            onClick={queueExport}
          >
            {createExport.isPending ? "Queueing…" : "Create export"}
          </button>
          <button
            className="button ghost"
            type="reset"
            onClick={() => setAppliedFilters({})}
          >
            Clear filters
          </button>
        </form>
        {createExport.isError ? (
          <p className="form-error" role="alert">
            {createExport.error.message}
          </p>
        ) : null}
        {exportJob.data === undefined ? null : (
          <div className="export-status" aria-live="polite">
            <strong>Export {exportJob.data.state}</strong>
            {exportJob.data.download_path === null ? (
              <span>The file will appear here when ready.</span>
            ) : (
              <button
                className="button secondary"
                type="button"
                disabled={downloadExport.isPending}
                onClick={() =>
                  downloadExport.mutate(exportJob.data.download_path ?? "")
                }
              >
                {downloadExport.isPending ? "Downloading…" : "Download CSV"}
              </button>
            )}
            {exportJob.data.expires_at === null ? null : (
              <span>
                Available until {formatDate(exportJob.data.expires_at)}.
              </span>
            )}
          </div>
        )}
        {downloadExport.isError ? (
          <p className="form-error" role="alert">
            {downloadExport.error.message}
          </p>
        ) : null}
      </section>
    </>
  );
}

export function PlantingsPage() {
  const [identifier, setIdentifier] = useState("");
  const [search, setSearch] = useState<string | null>(null);
  const history = useQuery({
    queryKey: ["planting-history", search],
    queryFn: () =>
      api<PlantingHistory>(
        `/admin/plantings/${encodeURIComponent(search ?? "")}/history`,
      ),
    enabled: search !== null,
  });
  return (
    <>
      <PageHeader
        eyebrow="Connected history"
        title="Plantings"
        description="Reconstruct requests, assignments, allowance, evidence, decisions, points, badges, and rank snapshots."
      />
      <form
        className="search-bar"
        onSubmit={(event) => {
          event.preventDefault();
          if (identifier.trim() !== "") setSearch(identifier.trim());
        }}
      >
        <label>
          Planting ID or public code
          <input
            value={identifier}
            onChange={(event) => setIdentifier(event.target.value)}
            placeholder="BT-2026-…"
            required
          />
        </label>
        <button className="button primary" type="submit">
          Reconstruct
        </button>
      </form>
      {search === null ? (
        <StatePanel
          kind="empty"
          title="Search by planting code"
          detail="Enter an approved public planting code or planting ID."
        />
      ) : history.isLoading ? (
        <StatePanel
          kind="loading"
          title="Reconstructing planting"
          detail="Reading every connected append-only record."
        />
      ) : history.isError || history.data === undefined ? (
        <StatePanel
          kind="error"
          title="Planting not available"
          detail="Check the code and your assigned scope."
          action={<RetryButton onClick={() => void history.refetch()} />}
        />
      ) : (
        <div className="history-grid">
          <article className="panel history-summary-card">
            <p className="eyebrow">Request</p>
            <h2>{String(history.data.request.public_code)}</h2>
            <p>
              {readableValue(String(history.data.request.state))} ·{" "}
              {Number(history.data.request.requested_quantity).toLocaleString()}{" "}
              requested
            </p>
            <small>
              Target {formatDate(String(history.data.request.target_date))}
            </small>
          </article>
          <article className="panel history-summary-card">
            <p className="eyebrow">Assignment</p>
            <h2>{readableValue(String(history.data.assignment.state))}</h2>
            <p>
              {Number(
                history.data.assignment.declared_plant_quantity,
              ).toLocaleString()}{" "}
              plants · version {String(history.data.assignment.current_version)}
            </p>
            <small>
              Planter {String(history.data.assignment.primary_planter_id)} ·
              Manager {String(history.data.assignment.responsible_manager_id)}
            </small>
          </article>
          <article className="panel">
            <p className="eyebrow">Planting</p>
            <h2>{String(history.data.planting.public_code)}</h2>
            <p>
              {readableValue(String(history.data.planting.state))} ·{" "}
              {readableValue(
                recordText(history.data.planting, "latest_condition") ??
                  "unknown",
              )}
            </p>
          </article>
          {historySections.map(([key, label]) => (
            <article className="panel history-records" key={key}>
              <p className="eyebrow">{label}</p>
              <strong className="history-count">
                {history.data[key].length}
              </strong>
              <small>connected record(s)</small>
              {history.data[key].length === 0 ? (
                <p className="quiet history-empty">No connected records.</p>
              ) : (
                <ol>
                  {history.data[key].map((record, index) => (
                    <li key={recordText(record, "id") ?? `${key}-${index}`}>
                      <strong>{historyRecordLabel(key, record)}</strong>
                      {historyRecordDate(record) === null ? null : (
                        <small>{historyRecordDate(record)}</small>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </article>
          ))}
        </div>
      )}
    </>
  );
}

export function PeoplePage() {
  const queryClient = useQueryClient();
  const [selectedUser, setSelectedUser] = useState("");
  const [workspace, setWorkspace] = useState<"register" | "access" | null>(
    null,
  );
  const [peopleSearch, setPeopleSearch] = useState("");
  const [scopeRole, setScopeRole] = useState("");
  const [personFormError, setPersonFormError] = useState("");
  const [accessFormError, setAccessFormError] = useState("");
  const people = useQuery({
    queryKey: ["admin-users"],
    queryFn: () =>
      api<{
        readonly items: readonly AdminPerson[];
      }>("/admin/users"),
  });
  const reference = useQuery({
    queryKey: ["reference"],
    queryFn: () =>
      api<{
        readonly organizations: readonly ReferenceOption[];
        readonly municipalities: readonly ReferenceOption[];
        readonly sites: readonly ReferenceOption[];
      }>("/reference-data"),
  });
  const plantTypes = useQuery({
    queryKey: ["admin-plant-types"],
    queryFn: () =>
      api<{
        readonly items: readonly {
          readonly id: string;
          readonly code: string;
          readonly common_name: string;
          readonly scientific_name: string | null;
          readonly native_status: string;
          readonly record_unit: string;
          readonly status: string;
        }[];
      }>("/admin/plant-types"),
  });
  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["admin-users"] }),
      queryClient.invalidateQueries({ queryKey: ["reference"] }),
      queryClient.invalidateQueries({ queryKey: ["admin-plant-types"] }),
    ]);
  };
  const createPerson = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api("/admin/users", {
        method: "POST",
        idempotencyKey: mutationKey("user"),
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await refresh();
      setWorkspace(null);
    },
  });
  const updateAccess = useMutation({
    mutationFn: ({
      userId,
      body,
    }: {
      readonly userId: string;
      readonly body: Record<string, unknown>;
    }) =>
      api(`/admin/users/${userId}/access`, {
        method: "PUT",
        idempotencyKey: mutationKey("user-access"),
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await refresh();
      setWorkspace(null);
    },
  });
  const createPlantType = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api("/admin/plant-types", {
        method: "POST",
        idempotencyKey: mutationKey("plant-type"),
        body: JSON.stringify(body),
      }),
    onSuccess: refresh,
  });
  const togglePlantType = useMutation({
    mutationFn: ({
      id,
      status,
    }: {
      readonly id: string;
      readonly status: string;
    }) =>
      api(`/admin/plant-types/${id}`, {
        method: "PATCH",
        body: JSON.stringify({
          status,
          reason: `${status === "active" ? "Restore" : "Retire"} approved planting option`,
        }),
      }),
    onSuccess: refresh,
  });
  const submitPerson = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const roles = data.getAll("roles");
    if (roles.length === 0) {
      setPersonFormError("Choose at least one initial role.");
      return;
    }
    setPersonFormError("");
    createPerson.mutate({
      account_kind: data.get("account_kind"),
      display_name: data.get("display_name"),
      public_display_name: data.get("public_display_name"),
      login_email: data.get("login_email"),
      initial_password: data.get("initial_password"),
      roles,
      ...(data.get("organization_id")
        ? { organization_id: data.get("organization_id") }
        : {}),
    });
  };
  const submitAccess = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const roles = data.getAll("access_roles").map(String);
    const chosenScopeRole = formString(data, "scope_role");
    const scopeValues = [
      "scope_organization_id",
      "scope_municipality_id",
      "scope_site_id",
    ].map((name) => formString(data, name));
    if (roles.length === 0) {
      setAccessFormError("Choose at least one role before saving.");
      return;
    }
    if (chosenScopeRole !== "" && scopeValues.every((value) => value === "")) {
      setAccessFormError(
        "Choose an organization, municipality, or site for the scoped role.",
      );
      return;
    }
    setAccessFormError("");
    const scope = {
      role: chosenScopeRole,
      ...Object.fromEntries(
        ["organization_id", "municipality_id", "site_id"]
          .map((key) => [key, formString(data, `scope_${key}`)])
          .filter(([, value]) => value !== ""),
      ),
    };
    updateAccess.mutate({
      userId: selectedUser,
      body: {
        roles,
        scopes: chosenScopeRole === "" ? [] : [scope],
        reason: data.get("reason"),
      },
    });
  };
  const submitPlantType = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    createPlantType.mutate({
      code: data.get("code"),
      common_name: data.get("common_name"),
      ...(data.get("scientific_name")
        ? { scientific_name: data.get("scientific_name") }
        : {}),
      native_status: data.get("native_status"),
      record_unit: data.get("record_unit"),
    });
  };
  const mutationError =
    createPerson.error ??
    updateAccess.error ??
    createPlantType.error ??
    togglePlantType.error;
  const selectedPerson = people.data?.items.find(
    (person) => person.id === selectedUser,
  );
  const normalizedSearch = peopleSearch.trim().toLowerCase();
  const filteredPeople =
    people.data?.items.filter((person) => {
      if (normalizedSearch === "") return true;
      return [
        person.display_name,
        person.id,
        person.account_kind,
        ...person.roles,
        ...person.scopes.flatMap((scope) => [
          scope.organization_name ?? "",
          scope.municipality_name ?? "",
          scope.site_name ?? "",
        ]),
      ].some((value) => value.toLowerCase().includes(normalizedSearch));
    }) ?? [];
  const staffCount =
    people.data?.items.filter((person) =>
      person.roles.some((role) => role !== "planter"),
    ).length ?? 0;
  const scopedCount =
    people.data?.items.filter((person) => person.scopes.length > 0).length ?? 0;

  const openAccessEditor = (userId = selectedUser) => {
    const person = people.data?.items.find((item) => item.id === userId);
    setSelectedUser(userId);
    setScopeRole(person?.scopes[0]?.role ?? "");
    setAccessFormError("");
    setWorkspace("access");
  };
  return (
    <>
      <PageHeader
        eyebrow="Administration"
        title="People and access"
        description="Keep program identities, staff responsibilities, and geographic access clear and auditable."
        actions={
          <button
            className="button primary"
            type="button"
            aria-expanded={workspace === "register"}
            aria-controls="register-person-editor"
            onClick={() =>
              setWorkspace((current) => {
                setPersonFormError("");
                return current === "register" ? null : "register";
              })
            }
          >
            <UserPlus aria-hidden="true" />
            Add person
          </button>
        }
      />
      <section className="people-principle" aria-label="Access principle">
        <ShieldCheck aria-hidden="true" />
        <div>
          <strong>Roles define what staff can do. Scope defines where.</strong>
          <p>
            Manager, verifier, and auditor access stays inactive outside an
            assigned organization, municipality, or site.
          </p>
        </div>
      </section>
      {mutationError ? (
        <StatePanel
          kind="error"
          title="Change not saved"
          detail={mutationError.message}
        />
      ) : null}
      {reference.isError ? (
        <StatePanel
          kind="error"
          title="Reference data unavailable"
          detail="Organization and geographic scope options could not be loaded."
          action={<RetryButton onClick={() => void reference.refetch()} />}
        />
      ) : null}
      {workspace === "register" ? (
        <section
          className="panel admin-editor"
          id="register-person-editor"
          aria-labelledby="register-person-title"
        >
          <header className="editor-heading">
            <div>
              <span className="editor-icon" aria-hidden="true">
                <UserPlus />
              </span>
              <div>
                <h2 id="register-person-title">Add a program person</h2>
                <p>
                  Create their record, public identity, membership, and initial
                  responsibilities.
                </p>
              </div>
            </div>
            <button
              className="icon-button"
              type="button"
              aria-label="Close add person form"
              onClick={() => setWorkspace(null)}
            >
              <X aria-hidden="true" />
            </button>
          </header>
          <form className="stack-form" onSubmit={submitPerson}>
            <section className="form-section" aria-labelledby="person-details">
              <div className="form-section-heading">
                <h3 id="person-details">Person details</h3>
                <p>Internal names stay private when a public name is set.</p>
              </div>
              <div className="field-row">
                <label>
                  Internal display name
                  <input
                    name="display_name"
                    required
                    minLength={2}
                    autoComplete="name"
                  />
                </label>
                <label>
                  Public display name
                  <input name="public_display_name" required minLength={2} />
                </label>
              </div>
              <div className="field-row">
                <label>
                  Account model
                  <select name="account_kind">
                    <option value="adult">Adult</option>
                    <option value="minor">Minor</option>
                    <option value="organization_managed">
                      Organization managed
                    </option>
                  </select>
                </label>
                <ReferenceSelect
                  name="organization_id"
                  label="Organization membership"
                  emptyLabel="No organization"
                  options={reference.data?.organizations}
                />
              </div>
            </section>
            <section className="form-section" aria-labelledby="account-login">
              <div className="form-section-heading with-icon">
                <KeyRound aria-hidden="true" />
                <div>
                  <h3 id="account-login">BalikTANiman sign-in</h3>
                  <p>
                    Create an application account. Share the initial password
                    through the approved private support channel.
                  </p>
                </div>
              </div>
              <div className="field-row">
                <label>
                  Login email
                  <input
                    name="login_email"
                    type="email"
                    autoComplete="off"
                    required
                  />
                </label>
                <label>
                  Initial password
                  <input
                    name="initial_password"
                    type="password"
                    autoComplete="new-password"
                    minLength={12}
                    maxLength={128}
                    required
                  />
                </label>
              </div>
            </section>
            <section className="form-section" aria-labelledby="initial-access">
              <div className="form-section-heading">
                <h3 id="initial-access">Initial responsibilities</h3>
                <p>
                  Choose at least one role. Staff scope is assigned afterward.
                </p>
              </div>
              <fieldset className="role-grid">
                <legend className="sr-only">Initial roles</legend>
                {accessRoles.map((role) => (
                  <label className="check-row" key={role}>
                    <input
                      type="checkbox"
                      name="roles"
                      value={role}
                      defaultChecked={role === "planter"}
                    />
                    <span>{role}</span>
                  </label>
                ))}
              </fieldset>
              {personFormError ? (
                <p className="field-error" role="alert">
                  {personFormError}
                </p>
              ) : null}
            </section>
            <div className="editor-actions">
              <button
                className="button secondary"
                type="button"
                onClick={() => setWorkspace(null)}
              >
                Cancel
              </button>
              <button
                className="button primary"
                disabled={createPerson.isPending}
              >
                {createPerson.isPending ? "Adding person…" : "Add person"}
              </button>
            </div>
          </form>
        </section>
      ) : null}
      {workspace === "access" ? (
        <section
          className="panel admin-editor"
          id="access-editor"
          aria-labelledby="access-editor-title"
        >
          <header className="editor-heading">
            <div>
              <span className="editor-icon" aria-hidden="true">
                <UserRoundCog />
              </span>
              <div>
                <h2 id="access-editor-title">Edit roles and scope</h2>
                <p>
                  Review the full replacement before saving. Earlier access
                  remains in the audit history.
                </p>
              </div>
            </div>
            <button
              className="icon-button"
              type="button"
              aria-label="Close access editor"
              onClick={() => setWorkspace(null)}
            >
              <X aria-hidden="true" />
            </button>
          </header>
          <form
            className="stack-form"
            onSubmit={submitAccess}
            key={selectedUser || "no-person"}
          >
            <label>
              Person
              <select
                required
                value={selectedUser}
                onChange={(event) => {
                  const userId = event.target.value;
                  const person = people.data?.items.find(
                    (item) => item.id === userId,
                  );
                  setSelectedUser(userId);
                  setScopeRole(person?.scopes[0]?.role ?? "");
                  setAccessFormError("");
                }}
              >
                <option value="">Choose person</option>
                {people.data?.items.map((person) => (
                  <option value={person.id} key={person.id}>
                    {person.display_name}
                  </option>
                ))}
              </select>
            </label>
            <section
              className="form-section"
              aria-labelledby="replacement-roles"
            >
              <div className="form-section-heading">
                <h3 id="replacement-roles">Roles after this change</h3>
                <p>
                  Unchecked roles will be revoked when this change is saved.
                </p>
              </div>
              <fieldset className="role-grid">
                <legend className="sr-only">Replacement roles</legend>
                {accessRoles.map((role) => (
                  <label className="check-row" key={role}>
                    <input
                      type="checkbox"
                      name="access_roles"
                      value={role}
                      defaultChecked={selectedPerson?.roles.includes(role)}
                    />
                    <span>{role}</span>
                  </label>
                ))}
              </fieldset>
            </section>
            <section
              className="form-section"
              aria-labelledby="geographic-scope"
            >
              <div className="form-section-heading with-icon">
                <MapPinned aria-hidden="true" />
                <div>
                  <h3 id="geographic-scope">Geographic scope</h3>
                  <p>
                    Add scope for manager, verifier, or auditor responsibility.
                  </p>
                </div>
              </div>
              <label>
                Scoped staff role
                <select
                  name="scope_role"
                  value={scopeRole}
                  onChange={(event) => setScopeRole(event.target.value)}
                >
                  <option value="">No geographic scope</option>
                  <option value="manager">Manager</option>
                  <option value="verifier">Verifier</option>
                  <option value="auditor">Auditor</option>
                </select>
              </label>
              {scopeRole === "" ? null : (
                <div className="field-row three-fields">
                  <ReferenceSelect
                    name="scope_organization_id"
                    label="Organization"
                    emptyLabel="Any organization"
                    defaultValue={
                      selectedPerson?.scopes[0]?.organization_id ?? ""
                    }
                    options={reference.data?.organizations}
                  />
                  <ReferenceSelect
                    name="scope_municipality_id"
                    label="Municipality"
                    emptyLabel="Any municipality"
                    defaultValue={
                      selectedPerson?.scopes[0]?.municipality_id ?? ""
                    }
                    options={reference.data?.municipalities}
                  />
                  <ReferenceSelect
                    name="scope_site_id"
                    label="Site"
                    emptyLabel="Any site"
                    defaultValue={selectedPerson?.scopes[0]?.site_id ?? ""}
                    options={reference.data?.sites}
                  />
                </div>
              )}
            </section>
            <label>
              Reason for change
              <textarea
                name="reason"
                required
                minLength={3}
                rows={3}
                placeholder="Explain the operational need for this access change"
              />
            </label>
            {accessFormError ? (
              <p className="field-error" role="alert">
                {accessFormError}
              </p>
            ) : null}
            <div className="access-impact" role="note">
              <ShieldCheck aria-hidden="true" />
              <p>
                <strong>This replaces current access.</strong> The server
                records the actor, reason, previous roles, and new scope.
              </p>
            </div>
            <div className="editor-actions">
              <button
                className="button secondary"
                type="button"
                onClick={() => setWorkspace(null)}
              >
                Cancel
              </button>
              <button
                className="button primary"
                disabled={updateAccess.isPending || selectedUser === ""}
              >
                {updateAccess.isPending ? "Saving access…" : "Save access"}
              </button>
            </div>
          </form>
        </section>
      ) : null}
      <section
        className="panel people-directory"
        aria-labelledby="people-directory-title"
      >
        <header className="people-directory-heading">
          <div>
            <h2 id="people-directory-title">People directory</h2>
            <p>Review current roles, operating scope, and account state.</p>
          </div>
          <div className="people-summary" aria-label="Directory summary">
            <span>
              <strong>{people.data?.items.length ?? 0}</strong> people
            </span>
            <span>
              <strong>{staffCount}</strong> staff
            </span>
            <span>
              <strong>{scopedCount}</strong> scoped
            </span>
          </div>
        </header>
        <div className="people-toolbar">
          <label className="search-field">
            <span className="sr-only">Search people</span>
            <Search aria-hidden="true" />
            <input
              type="search"
              value={peopleSearch}
              placeholder="Search name, role, or scope"
              onChange={(event) => setPeopleSearch(event.target.value)}
            />
          </label>
          <button
            className="button secondary"
            type="button"
            aria-expanded={workspace === "access"}
            aria-controls="access-editor"
            onClick={() => openAccessEditor()}
          >
            <UserRoundCog aria-hidden="true" />
            Manage access
          </button>
        </div>
        {people.isLoading ? (
          <StatePanel
            kind="loading"
            title="Loading people"
            detail="Checking active roles and scopes."
          />
        ) : people.isError ? (
          <StatePanel
            kind="error"
            title="People unavailable"
            detail="No access was changed."
            action={<RetryButton onClick={() => void people.refetch()} />}
          />
        ) : people.data?.items.length === 0 ? (
          <StatePanel
            kind="empty"
            title="No people registered"
            detail="Add the first approved program person to begin assigning responsibility."
          />
        ) : filteredPeople.length === 0 ? (
          <StatePanel
            kind="empty"
            title="No matching people"
            detail="Try a different name, role, account type, or scope."
          />
        ) : (
          <div className="table-wrap">
            <table className="people-table">
              <caption className="sr-only">
                People, roles, geographic scopes, and account status
              </caption>
              <thead>
                <tr>
                  <th scope="col">Person</th>
                  <th scope="col">Account</th>
                  <th scope="col">Roles</th>
                  <th scope="col">Active scope</th>
                  <th scope="col">Status</th>
                  <th scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredPeople.map((person) => (
                  <tr key={person.id}>
                    <td data-label="Person">
                      <div className="person-cell">
                        <span className="person-avatar" aria-hidden="true">
                          {person.display_name.slice(0, 1).toUpperCase()}
                        </span>
                        <span>
                          <strong>{person.display_name}</strong>
                          <small className="record-code">{person.id}</small>
                        </span>
                      </div>
                    </td>
                    <td data-label="Account">
                      {readableValue(person.account_kind)}
                    </td>
                    <td data-label="Roles">
                      <span className="role-list">
                        {person.roles.length === 0 ? (
                          <span className="quiet">None</span>
                        ) : (
                          person.roles.map((role) => (
                            <span className="role-chip" key={role}>
                              {readableValue(role)}
                            </span>
                          ))
                        )}
                      </span>
                    </td>
                    <td data-label="Active scope">
                      {person.scopes.length === 0 ? (
                        <span className="quiet">No geographic scope</span>
                      ) : (
                        <ul className="scope-list">
                          {person.scopes.map((scope) => (
                            <li key={scope.id}>
                              <strong>{readableValue(scope.role)}</strong>
                              <span>
                                {scope.site_name ??
                                  scope.municipality_name ??
                                  scope.organization_name ??
                                  "Province-wide"}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                    <td data-label="Status">
                      <span
                        className={`status-chip ${
                          person.status === "active" ? "success" : "warning"
                        }`}
                      >
                        {readableValue(person.status)}
                      </span>
                    </td>
                    <td className="people-row-action">
                      <button
                        className="text-button"
                        type="button"
                        onClick={() => openAccessEditor(person.id)}
                      >
                        Edit access
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <details className="catalog-disclosure">
        <summary>
          <span>
            <strong>Planting catalog</strong>
            <small>Approved plant types and tracking units</small>
          </span>
        </summary>
        <div className="admin-grid catalog-grid">
          <section className="panel">
            <h2>Add approved plant type</h2>
            <form className="stack-form" onSubmit={submitPlantType}>
              <label>
                Code
                <input name="code" required pattern="[a-z0-9-]+" />
              </label>
              <label>
                Common name
                <input name="common_name" required minLength={2} />
              </label>
              <label>
                Scientific name
                <input name="scientific_name" />
              </label>
              <label>
                Native status
                <select name="native_status">
                  <option value="native">Native</option>
                  <option value="non_native">Non-native</option>
                  <option value="unknown">Unknown</option>
                </select>
              </label>
              <label>
                Tracking unit
                <select name="record_unit">
                  <option value="individual">Individual plant</option>
                  <option value="managed_batch">Managed batch</option>
                </select>
              </label>
              <button
                className="button primary"
                disabled={createPlantType.isPending}
              >
                Add plant type
              </button>
            </form>
          </section>
          <section className="panel">
            <h2>Approved plant types</h2>
            {plantTypes.isLoading ? (
              <StatePanel
                kind="loading"
                title="Loading approved plant types"
                detail="Reading the current planting catalog."
              />
            ) : plantTypes.isError || plantTypes.data === undefined ? (
              <StatePanel
                kind="error"
                title="Plant types unavailable"
                detail="No catalog entry was changed."
                action={
                  <RetryButton onClick={() => void plantTypes.refetch()} />
                }
              />
            ) : plantTypes.data.items.length === 0 ? (
              <StatePanel
                kind="empty"
                title="No approved plant types"
                detail="Add the first forestry-approved plant type."
              />
            ) : (
              <div className="plant-type-list">
                {plantTypes.data.items.map((plant) => (
                  <article key={plant.id}>
                    <div>
                      <strong>{plant.common_name}</strong>
                      <small>
                        {plant.scientific_name ?? "No scientific name"} ·{" "}
                        {plant.record_unit.replaceAll("_", " ")}
                      </small>
                    </div>
                    <button
                      className="button secondary"
                      disabled={togglePlantType.isPending}
                      onClick={() =>
                        togglePlantType.mutate({
                          id: plant.id,
                          status:
                            plant.status === "active" ? "inactive" : "active",
                        })
                      }
                    >
                      {plant.status === "active" ? "Retire" : "Restore"}
                    </button>
                  </article>
                ))}
              </div>
            )}
          </section>
        </div>
      </details>
    </>
  );
}

export function AuditPage() {
  const [filters, setFilters] = useState<Readonly<Record<string, string>>>({});
  const filterQuery = new URLSearchParams(filters).toString();
  const query = useQuery({
    queryKey: ["audit", filters],
    queryFn: () =>
      api<{
        readonly items: readonly {
          readonly id: string;
          readonly action: string;
          readonly target_type: string;
          readonly target_id: string;
          readonly actor_user_id: string;
          readonly reason: string | null;
          readonly created_at: string;
        }[];
      }>(`/admin/audit${filterQuery === "" ? "" : `?${filterQuery}`}`),
  });
  return (
    <>
      <PageHeader
        eyebrow="Immutable history"
        title="Audit log"
        description="Privileged mutations, exact-location access, exports, and policy publication."
      />
      <form
        className="filter-bar audit-filter-bar"
        aria-label="Audit log filters"
        onSubmit={(event) => {
          event.preventDefault();
          const data = new FormData(event.currentTarget);
          setFilters(
            Object.fromEntries(
              [
                "action",
                "actor_user_id",
                "target_type",
                "target_id",
                "starts_at",
                "ends_at",
              ]
                .map((key) => [key, formString(data, key)] as const)
                .filter(([, value]) => value !== "")
                .map(([key, value]) => [
                  key,
                  key === "starts_at" || key === "ends_at"
                    ? new Date(value).toISOString()
                    : value,
                ]),
            ),
          );
        }}
      >
        <label>
          Action
          <input name="action" placeholder="export.downloaded" />
        </label>
        <label>
          Actor ID
          <input name="actor_user_id" />
        </label>
        <label>
          Target type
          <input name="target_type" placeholder="planting_post" />
        </label>
        <label>
          Target ID
          <input name="target_id" />
        </label>
        <label>
          From
          <input name="starts_at" type="datetime-local" />
        </label>
        <label>
          To
          <input name="ends_at" type="datetime-local" />
        </label>
        <button className="button primary" type="submit">
          Apply filters
        </button>
        <button
          className="button ghost"
          type="reset"
          onClick={() => setFilters({})}
        >
          Clear filters
        </button>
      </form>
      {query.isLoading ? (
        <StatePanel
          kind="loading"
          title="Loading audit records"
          detail="Reading append-only operational history."
        />
      ) : query.isError || query.data === undefined ? (
        <StatePanel
          kind="error"
          title="Audit records are unavailable"
          detail="Try the scoped audit search again."
          action={<RetryButton onClick={() => void query.refetch()} />}
        />
      ) : query.data.items.length === 0 ? (
        <StatePanel
          kind="empty"
          title="No audit records"
          detail="Privileged actions and exact-evidence access will appear here."
        />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th scope="col">Time</th>
                <th scope="col">Action</th>
                <th scope="col">Target</th>
                <th scope="col">Actor</th>
                <th scope="col">Reason</th>
              </tr>
            </thead>
            <tbody>
              {query.data.items.map((item) => (
                <tr key={item.id}>
                  <td>{formatDate(item.created_at)}</td>
                  <td>{item.action}</td>
                  <td>
                    {item.target_type}
                    <small className="record-code">{item.target_id}</small>
                  </td>
                  <td>{item.actor_user_id}</td>
                  <td>{item.reason ?? "Not supplied"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

export function NotificationsPage() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["notifications"],
    queryFn: () =>
      api<{
        readonly unread_count: number;
        readonly items: readonly {
          readonly id: string;
          readonly title: string;
          readonly body: string;
          readonly read_at: string | null;
          readonly created_at: string;
        }[];
      }>("/notifications"),
  });
  const markRead = useMutation({
    mutationFn: (input: {
      readonly notification_ids?: readonly string[];
      readonly all?: true;
    }) =>
      api<{ readonly read_at: string }>("/notifications/read", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["notifications"] }),
  });
  return (
    <>
      <PageHeader
        eyebrow="Inbox"
        title="Notifications"
        description={`${query.data?.unread_count ?? 0} unread program notice(s).`}
        actions={
          (query.data?.unread_count ?? 0) > 0 ? (
            <button
              className="button secondary"
              type="button"
              disabled={markRead.isPending}
              onClick={() => markRead.mutate({ all: true })}
            >
              {markRead.isPending ? "Updating…" : "Mark all as read"}
            </button>
          ) : undefined
        }
      />
      {markRead.isError ? (
        <div className="inline-alert" role="alert">
          Notifications could not be updated. No read state was changed. Try
          again.
        </div>
      ) : null}
      {query.isLoading ? (
        <StatePanel
          kind="loading"
          title="Loading notifications"
          detail="Reading assignment, review, and recognition notices."
        />
      ) : query.isError || query.data === undefined ? (
        <StatePanel
          kind="error"
          title="Notifications unavailable"
          detail="No notice was changed. Try loading the inbox again."
          action={<RetryButton onClick={() => void query.refetch()} />}
        />
      ) : query.data.items.length === 0 ? (
        <StatePanel
          kind="empty"
          title="Inbox clear"
          detail="Assignment, allowance, review, badge, rank, and follow-up notices appear here."
        />
      ) : (
        <div className="record-list">
          {query.data.items.map((item) => (
            <article
              className={`notification-row${item.read_at === null ? " unread" : ""}`}
              key={item.id}
            >
              <span
                className={item.read_at === null ? "unread-dot" : "read-dot"}
                aria-hidden="true"
              />
              <div className="notification-content">
                <h2>{item.title}</h2>
                <p>{item.body}</p>
                <small>
                  {item.read_at === null ? "Unread" : "Read"} ·{" "}
                  {formatDate(item.created_at)}
                </small>
              </div>
              {item.read_at === null ? (
                <button
                  className="text-button notification-read-action"
                  type="button"
                  disabled={markRead.isPending}
                  onClick={() =>
                    markRead.mutate({ notification_ids: [item.id] })
                  }
                  aria-label={`Mark ${item.title} as read`}
                >
                  Mark as read
                </button>
              ) : null}
            </article>
          ))}
        </div>
      )}
    </>
  );
}
